import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";

import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  condicaoPadraoDoProduto,
  resolveLineDiscount,
  resolveLineEncargo,
  netUnitPrice,
} from "../utils/paymentConditionUtils";
import { MARCA_NUM_TO_BRAND_KEY } from "../utils/family-builder";
import { resolveOrderLineFromKey } from "./resolveOrderLine";

const CART_STORAGE_KEY = "vitrine-empresa-cart";

function normalizeBrandCart(stored) {
  if (!stored || typeof stored !== "object") {
    return { order: {}, vitrinePaymentConditions: {} };
  }
  if ("order" in stored || "vitrinePaymentConditions" in stored) {
    return {
      order:
        stored.order && typeof stored.order === "object" ? stored.order : {},
      vitrinePaymentConditions:
        stored.vitrinePaymentConditions &&
        typeof stored.vitrinePaymentConditions === "object"
          ? stored.vitrinePaymentConditions
          : {},
    };
  }
  return { order: stored, vitrinePaymentConditions: {} };
}

function brandCartHasItems(cart) {
  const normalized = normalizeBrandCart(cart);
  return Object.keys(normalized.order).length > 0;
}

// Exportadas pra useOrderSubmit.js: o carrinho é persistido por marca (localStorage), mas
// finalizar o pedido precisa enxergar TODAS as marcas com item pendente, não só a ativa —
// orderState (estado React) só tem a marca corrente; as demais ficam só aqui no storage.
export function getCartFromStorage(brandKeys = []) {
  try {
    if (!brandKeys || brandKeys.length === 0) return {};
    const raw =
      typeof window !== "undefined"
        ? localStorage.getItem(CART_STORAGE_KEY)
        : null;
    if (!raw) {
      const result = {};
      brandKeys.forEach((b) => {
        result[b] = { order: {}, vitrinePaymentConditions: {} };
      });
      return result;
    }
    const parsed = JSON.parse(raw);
    const result = {};
    brandKeys.forEach((b) => {
      result[b] = normalizeBrandCart(
        parsed[b] && typeof parsed[b] === "object" ? parsed[b] : {},
      );
    });
    return result;
  } catch {
    const result = {};
    (brandKeys || []).forEach((b) => {
      result[b] = { order: {}, vitrinePaymentConditions: {} };
    });
    return result;
  }
}

function getCartForBrand(brand, brandKeys = []) {
  const all = getCartFromStorage(brandKeys);
  return normalizeBrandCart(all[brand]);
}

/**
 * Junta o orderState/condições da marca ATIVA (estado React, sempre o mais atual) com o
 * carrinho salvo de cada OUTRA marca (localStorage) — sem isso, itens de uma vitrine de
 * outra marca ficam só "estacionados" ao trocar de marca e nunca aparecem numa varredura
 * que só olha pro orderState do componente (ex.: finalizar pedido, seleção de loja).
 * Retorna `stored` (o snapshot lido do storage) pra quem precisar regravar por marca depois
 * (ex.: useOrderSubmit ao limpar só os itens já confirmados).
 */
export function mergeOrderStateAcrossBrands({
  orderState,
  vitrinePaymentConditionState,
  currentBrand,
  brandKeys,
}) {
  const stored = getCartFromStorage(brandKeys);
  const fullOrderState = {};
  const fullVitrinePaymentConditionState = {};
  (brandKeys || []).forEach((brand) => {
    const isCurrent = brand === currentBrand;
    const brandOrder = isCurrent ? orderState : stored[brand]?.order || {};
    const brandConditions = isCurrent
      ? vitrinePaymentConditionState
      : stored[brand]?.vitrinePaymentConditions || {};
    Object.assign(fullOrderState, brandOrder);
    Object.assign(fullVitrinePaymentConditionState, brandConditions);
  });
  return { fullOrderState, fullVitrinePaymentConditionState, stored };
}

export function saveCartForBrand(brand, order, vitrinePaymentConditions, brandKeys = []) {
  try {
    const all = getCartFromStorage(brandKeys);
    all[brand] = {
      order: order && typeof order === "object" ? order : {},
      vitrinePaymentConditions:
        vitrinePaymentConditions &&
        typeof vitrinePaymentConditions === "object"
          ? vitrinePaymentConditions
          : {},
    };
    if (typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(all));
    }
  } catch (e) {
    console.error("Erro ao salvar carrinho no storage:", e);
  }
}

/**
 * Estado de marca ativa + carrinho persistido por marca (localStorage), incluindo
 * totais e as ações de trocar de marca / limpar pedido / remover item (via useDialogs).
 */
export default function useBrandCart({
  vitrineApiResult,
  effectiveBrandKeys,
  availableBrandKeys,
  primaryVitrine,
  effectiveFamilies,
  openConfirm,
  closeConfirm,
}) {
  const [showIntroScreen, setShowIntroScreen] = useState(true);
  const [currentBrand, setCurrentBrand] = useState("");
  const [orderState, setOrderState] = useState({});
  const [vitrinePaymentConditionState, setVitrinePaymentConditionState] =
    useState({});
  const [isCartGrouped, setIsCartGrouped] = useState(false);

  const theme =
    themeColorsDesingSystem.brands[
      currentBrand === "karsten_decor" ? "karsten" : currentBrand
    ] || themeColorsDesingSystem.brands.karsten;

  const canSwitchBrand = availableBrandKeys.length > 1;
  const shouldShowIntroScreen =
    showIntroScreen && availableBrandKeys.length !== 1;

  useLayoutEffect(() => {
    if (!vitrineApiResult || effectiveBrandKeys.length === 0) return;

    const all = getCartFromStorage(effectiveBrandKeys);
    const brandWithCart = effectiveBrandKeys.find((b) =>
      brandCartHasItems(all[b]),
    );

    if (brandWithCart) {
      setCurrentBrand(brandWithCart);
      const cart = getCartForBrand(brandWithCart, effectiveBrandKeys);
      setOrderState(cart.order);
      setVitrinePaymentConditionState(cart.vitrinePaymentConditions);
      setShowIntroScreen(false);
      return;
    }

    if (availableBrandKeys.length === 1) {
      const onlyBrand = availableBrandKeys[0];
      setCurrentBrand(onlyBrand);
      const cart = getCartForBrand(onlyBrand, effectiveBrandKeys);
      setOrderState(cart.order);
      setVitrinePaymentConditionState(cart.vitrinePaymentConditions);
      setShowIntroScreen(false);
      return;
    }

    setShowIntroScreen(true);
  }, [vitrineApiResult, effectiveBrandKeys, availableBrandKeys]);

  useEffect(() => {
    if (
      !shouldShowIntroScreen &&
      primaryVitrine &&
      effectiveBrandKeys.length > 0 &&
      !effectiveBrandKeys.includes(currentBrand)
    ) {
      const brandKeyFromMarca =
        primaryVitrine.Marca != null
          ? MARCA_NUM_TO_BRAND_KEY[primaryVitrine.Marca]
          : null;
      setCurrentBrand(
        brandKeyFromMarca && effectiveBrandKeys.includes(brandKeyFromMarca)
          ? brandKeyFromMarca
          : effectiveBrandKeys[0],
      );
    }
  }, [shouldShowIntroScreen, primaryVitrine, effectiveBrandKeys, currentBrand]);

  // saveCartForBrand relê e reescreve o localStorage inteiro (JSON.parse/stringify de todas as
  // marcas) de forma síncrona. orderState muda a cada tecla digitada num campo de quantidade —
  // sem debounce, cada tecla travava a digitação com esse round-trip de serialização.
  const debouncedSaveCartRef = useRef(
    debounce((brand, order, paymentConditions, brandKeys) => {
      saveCartForBrand(brand, order, paymentConditions, brandKeys);
    }, 400),
  );

  useEffect(() => {
    if (effectiveBrandKeys.length === 0) return;
    debouncedSaveCartRef.current(
      currentBrand,
      orderState,
      vitrinePaymentConditionState,
      effectiveBrandKeys,
    );
  }, [currentBrand, orderState, vitrinePaymentConditionState, effectiveBrandKeys]);

  useEffect(() => {
    const debouncedSave = debouncedSaveCartRef.current;
    return () => debouncedSave.flush();
  }, []);

  // Objeto estável com apenas as famílias da marca atual: serve de chave para o
  // cache do índice de linhas do carrinho. Antes, um literal novo era criado dentro
  // do laço, uma vez por item, forçando a varredura completa da vitrine a cada item.
  const familiesDaMarca = useMemo(
    () => ({ [currentBrand]: effectiveFamilies[currentBrand] }),
    [currentBrand, effectiveFamilies],
  );

  const totals = useMemo(() => {
    let qty = 0;
    let val = 0;

    Object.entries(orderState).forEach(([key, quantity]) => {
      const resolved = resolveOrderLineFromKey(key, familiesDaMarca);
      if (!resolved) return;
      const { prod, fam } = resolved;
      const idCond =
        vitrinePaymentConditionState[prod.vitrine] ??
        condicaoPadraoDoProduto(prod, fam);
      const price = netUnitPrice(
        prod,
        resolveLineDiscount(prod, fam, idCond),
        resolveLineEncargo(fam, idCond, prod),
      );
      qty += Number(quantity) || 0;
      val += (Number(quantity) || 0) * price;
    });

    return { qty, val };
  }, [orderState, vitrinePaymentConditionState, familiesDaMarca]);

  const clearOrder = () => {
    openConfirm({
      title: "Limpar Pedido",
      message: "Tem certeza que deseja remover todos os itens do pedido?",
      callback: () => {
        setOrderState({});
        setVitrinePaymentConditionState({});
        closeConfirm();
      },
    });
  };

  const removeFromCart = (key) => {
    openConfirm({
      title: "Remover Item",
      message: "Deseja remover este item do pedido?",
      callback: () => {
        const newOrderState = { ...orderState };
        delete newOrderState[key];
        setOrderState(newOrderState);
        closeConfirm();
      },
    });
  };

  const setVitrinePaymentCondition = (vitrineKey, idCondicaoPagamento) => {
    if (!vitrineKey) return;
    setVitrinePaymentConditionState((prev) => ({
      ...prev,
      [vitrineKey]: idCondicaoPagamento,
    }));
  };

  const handleSelectBrand = (name) => {
    saveCartForBrand(
      currentBrand,
      orderState,
      vitrinePaymentConditionState,
      effectiveBrandKeys,
    );
    setCurrentBrand(name);
    const cart = getCartForBrand(name, effectiveBrandKeys);
    setOrderState(cart.order);
    setVitrinePaymentConditionState(cart.vitrinePaymentConditions);
    setShowIntroScreen(false);
  };

  const handleRequestShowIntro = () => {
    if (Object.keys(orderState).length > 0) {
      openConfirm({
        title: "Trocar de marca",
        message: "Deseja trocar de marca? O carrinho atual será limpo.",
        callback: () => {
          saveCartForBrand(currentBrand, {}, {}, effectiveBrandKeys);
          setOrderState({});
          setVitrinePaymentConditionState({});
          setShowIntroScreen(true);
          closeConfirm();
        },
      });
    } else {
      setShowIntroScreen(true);
    }
  };

  return {
    showIntroScreen,
    shouldShowIntroScreen,
    canSwitchBrand,
    currentBrand,
    theme,
    orderState,
    setOrderState,
    vitrinePaymentConditionState,
    setVitrinePaymentConditionState,
    setVitrinePaymentCondition,
    isCartGrouped,
    toggleCartGrouping: () => setIsCartGrouped((prev) => !prev),
    totals,
    clearOrder,
    removeFromCart,
    handleSelectBrand,
    handleRequestShowIntro,
  };
}
